import { HEADER_BYTES, OK, checkHeader, putHeader, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `vitals` frame (ADR-074 decision 5, PROTOCOL.md 5.5): 16 bytes, server to client, the player's own exact
 * health, Stamina and Focus with their maximums and a state byte. It reaches only the player it describes; every
 * watcher reads the Health component's percentage instead. The encoder writes into a buffer the session allocates at
 * join through plain byte stores, so a send allocates nothing; the decoder never throws (ADR-030).
 */

/**
 * Total frame length.
 */
export const VITALS_BYTES = 16;

/**
 * Bits of the state byte. A page ignores the others, which the server writes as 0. `InPvpCombat` is set while the
 * player struck a player or was struck by one within the combat window, which halves a consumable's heal (ADR-091's
 * amendment of 2026-10-08); a page that predates it reads it as an unnamed bit and ignores it.
 */
export const VitalsState = { Dead: 1, InCombat: 2, InPvpCombat: 4 } as const;

const KNOWN_STATE = VitalsState.Dead | VitalsState.InCombat | VitalsState.InPvpCombat;

/**
 * A decoded or to-be-encoded `vitals`, reused across frames.
 */
export interface VitalsRecord {
  health: number;
  maxHealth: number;
  stamina: number;
  maxStamina: number;
  focus: number;
  maxFocus: number;
  state: number;
}

/**
 * A fresh record for `decodeVitals` to fill.
 *
 * @returns A zeroed record.
 */
export function newVitalsRecord(): VitalsRecord {
  return { health: 0, maxHealth: 0, stamina: 0, maxStamina: 0, focus: 0, maxFocus: 0, state: 0 };
}

/**
 * Encodes a `vitals` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `VITALS_BYTES` long.
 * @param vitals The values.
 * @returns Bytes written, always `VITALS_BYTES`.
 * @throws Error when the buffer is too small, a value is not a `u16` or the state sets a bit the layout does not
 *   name.
 */
export function encodeVitals(buffer: Uint8Array, vitals: Readonly<VitalsRecord>): number {
  if (buffer.length < VITALS_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${VITALS_BYTES}-byte vitals`);
  }
  if (!Number.isInteger(vitals.state) || vitals.state < 0 || (vitals.state & ~KNOWN_STATE) !== 0) {
    throw new Error(`vitals state ${vitals.state} sets bits the layout does not name`);
  }
  putHeader(buffer, MessageType.Vitals);
  putU16(buffer, HEADER_BYTES, vitals.health, "health");
  putU16(buffer, HEADER_BYTES + 2, vitals.maxHealth, "maxHealth");
  putU16(buffer, HEADER_BYTES + 4, vitals.stamina, "stamina");
  putU16(buffer, HEADER_BYTES + 6, vitals.maxStamina, "maxStamina");
  putU16(buffer, HEADER_BYTES + 8, vitals.focus, "focus");
  putU16(buffer, HEADER_BYTES + 10, vitals.maxFocus, "maxFocus");
  buffer[HEADER_BYTES + 12] = vitals.state;
  return VITALS_BYTES;
}

/**
 * Decodes a `vitals` frame into a record. Never throws: a wrong length or type is malformed and a wrong version a
 * mismatch, and every value of every field is well formed, the state's unnamed bits included.
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeVitals(frame: Uint8Array, out: VitalsRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.Vitals, VITALS_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  out.health = u16At(frame, HEADER_BYTES);
  out.maxHealth = u16At(frame, HEADER_BYTES + 2);
  out.stamina = u16At(frame, HEADER_BYTES + 4);
  out.maxStamina = u16At(frame, HEADER_BYTES + 6);
  out.focus = u16At(frame, HEADER_BYTES + 8);
  out.maxFocus = u16At(frame, HEADER_BYTES + 10);
  out.state = frame[HEADER_BYTES + 12] as number;
  return OK;
}

/**
 * Writes a little-endian `u16`.
 *
 * @param buffer The frame.
 * @param offset Where.
 * @param value The value.
 * @param field The field's name, for the error.
 * @throws Error when the value is not a whole number from 0 to 65,535.
 */
function putU16(buffer: Uint8Array, offset: number, value: number, field: string): void {
  if (!Number.isInteger(value) || value < 0 || value > MAX_U16) {
    throw new Error(`vitals ${field} ${value} is not a u16`);
  }
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = value >>> 8;
}

/**
 * Reads a little-endian `u16` from a frame whose length has been checked.
 *
 * @param frame The frame.
 * @param offset Where.
 * @returns The value.
 */
function u16At(frame: Uint8Array, offset: number): number {
  return (frame[offset] as number) | ((frame[offset + 1] as number) << 8);
}
